/**
 * The email body editor of the message composer.
 *
 * Its value is a ProseMirror document (`editor.getJSON()`), NOT HTML: the
 * server renders it per recipient (`messaging/pm_html.py`), escaping every
 * value as it writes it. So this editor deliberately does not route through
 * `sanitizeRichHtml` — a merge field's key lives in a node attribute that an
 * HTML round-trip would strip.
 *
 * The two document-form atoms are reused from the DMS authoring extensions:
 * `mergeField` (`{key, format, fallback}`) and `repeatBlock` (`{key}` —
 * `records` = the message's records, `form:<n>` = a record's child lines).
 * Here they get small node views of their own: a chip, and a dashed box that
 * says what it repeats over.
 */
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react'
import {
  EditorContent, NodeViewContent, NodeViewWrapper, ReactNodeViewRenderer, useEditor,
  type NodeViewProps,
} from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import { Table } from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableHeader from '@tiptap/extension-table-header'
import TableCell from '@tiptap/extension-table-cell'
import { Bold, Italic, Link2, Table as TableIcon, Underline as UnderlineIcon } from 'lucide-react'
import { MergeField, RepeatBlock } from '../dms/authoring/extensions'
import type { PmDoc, SendFields } from './types'

export interface MessageBodyEditorHandle {
  /** Insert a merge field at the cursor. */
  insertField: (key: string, format?: string) => void
  focus: () => void
}

/** What a merge key reads as on a chip. */
export function fieldLabel(key: string, fields?: SendFields): string {
  const k = String(key || '')
  if (!fields) return k
  const low = k.toLowerCase()
  if (low.startsWith('lookup.')) {
    const [, obj, ...rest] = k.split('.')
    const col = rest.join('.')
    const lk = fields.lookups.find((l) => l.obj.toLowerCase() === String(obj).toLowerCase())
    const c = lk?.columns.find((x) => x.key.toLowerCase() === col.toLowerCase()
      || x.db_name.toLowerCase() === col.toLowerCase())
    return `${obj}.${c?.heading || col}`
  }
  const tok = [...fields.group, ...fields.system].find((t) => t.key.toLowerCase() === low)
  if (tok) return tok.heading
  for (const c of fields.header) {
    if (c.key.toLowerCase() === low || c.db_name.toLowerCase() === low) return c.heading
  }
  for (const f of fields.forms) {
    for (const c of f.columns) {
      if (c.key.toLowerCase() === low || c.db_name.toLowerCase() === low) return c.heading
    }
  }
  return k
}

export function repeatLabel(key: string, fields?: SendFields): string {
  const k = String(key || '')
  if (k === 'records') return 'Repeat for each record in the message'
  if (k.toLowerCase().startsWith('form:')) {
    const no = k.split(':')[1]
    const f = fields?.forms.find((x) => x.form_no === no)
    return `Repeat for each line of ${f?.title || `form ${no}`}`
  }
  return `Repeat over ${k}`
}

const chip: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', borderRadius: 4, padding: '0 6px', margin: '0 1px',
  background: 'var(--color-accent-weak, rgba(0,97,208,.10))', color: 'var(--color-accent, #0052b0)',
  fontSize: 13, fontWeight: 500, lineHeight: 1.6, whiteSpace: 'nowrap', userSelect: 'none',
}

function makeViews(fieldsRef: { current?: SendFields }) {
  function MergeFieldView({ node, selected }: NodeViewProps) {
    const a = node.attrs as { key?: string; format?: string; fallback?: string }
    return (
      <NodeViewWrapper as="span" data-merge-field="true" style={{
        ...chip, outline: selected ? '2px solid var(--color-accent, #0061D0)' : undefined,
      }} title={`{${a.key || ''}}${a.format ? ` · ${a.format}` : ''}${a.fallback ? ` · if blank: ${a.fallback}` : ''}`}>
        {`{${fieldLabel(a.key || '', fieldsRef.current)}}`}
      </NodeViewWrapper>
    )
  }
  function RepeatBlockView({ node }: NodeViewProps) {
    const key = String((node.attrs as { key?: string }).key || '')
    return (
      <NodeViewWrapper data-repeat-block="true" style={{
        border: '1px dashed var(--color-accent, #0061D0)', borderRadius: 6, padding: '6px 10px',
        margin: '6px 0',
      }}>
        <div contentEditable={false} style={{
          fontSize: 13, fontWeight: 600, color: 'var(--color-accent, #0052b0)', marginBottom: 4,
          userSelect: 'none',
        }}>↻ {repeatLabel(key, fieldsRef.current)}</div>
        <NodeViewContent />
      </NodeViewWrapper>
    )
  }
  return { MergeFieldView, RepeatBlockView }
}

const toolBtn: React.CSSProperties = {
  font: 'inherit', fontSize: 13, minHeight: 30, padding: '0 8px', border: '1px solid transparent',
  borderRadius: 4, background: 'transparent', color: 'var(--color-text, #1b2330)', cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 4,
}

const toolSelect: React.CSSProperties = {
  height: 30, fontSize: 13, borderRadius: 4, padding: '0 6px', fontFamily: 'inherit',
  border: '1px solid var(--color-border-strong, #c9d1db)', background: 'var(--color-input-bg, #fff)',
  color: 'var(--color-text, #1b2330)', maxWidth: 220,
}

export const MessageBodyEditor = forwardRef<MessageBodyEditorHandle, {
  value: PmDoc
  onChange: (doc: PmDoc) => void
  fields?: SendFields
  /** "+ Include report / visual" — the composer adds an attachment row. */
  onInclude?: () => void
  onFocus?: () => void
  disabled?: boolean
  minHeight?: number
}>(function MessageBodyEditor({ value, onChange, fields, onInclude, onFocus, disabled, minHeight = 260 }, ref) {
  const fieldsRef = useRef<SendFields | undefined>(fields)
  fieldsRef.current = fields
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const onFocusRef = useRef(onFocus)
  onFocusRef.current = onFocus

  const extensions = useMemo(() => {
    const { MergeFieldView, RepeatBlockView } = makeViews(fieldsRef)
    return [
      StarterKit.configure({ heading: { levels: [2, 3, 4] } }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true }),
      Table.configure({ resizable: false }), TableRow, TableHeader, TableCell,
      MergeField.extend({ addNodeView: () => ReactNodeViewRenderer(MergeFieldView) }),
      RepeatBlock.extend({ addNodeView: () => ReactNodeViewRenderer(RepeatBlockView) }),
    ]
  }, [])

  const editor = useEditor({
    extensions,
    content: ((value && value.type === 'doc') ? value : { type: 'doc', content: [{ type: 'paragraph' }] }) as never,
    editable: !disabled,
    onUpdate: ({ editor: ed }) => {
      try {
        onChangeRef.current(ed.getJSON() as PmDoc)
      } catch (err) {
        console.warn('[MessageBodyEditor] onChange failed', err)
      }
    },
    onFocus: () => onFocusRef.current?.(),
  })

  // A different template loaded from outside → replace the document (not on
  // every keystroke: only when the incoming doc differs from what we hold).
  const lastSet = useRef<string>('')
  useEffect(() => {
    if (!editor) return
    const incoming = JSON.stringify(value || {})
    const current = JSON.stringify(editor.getJSON())
    if (incoming !== current && incoming !== lastSet.current) {
      lastSet.current = incoming
      try {
        editor.commands.setContent(value && value.type === 'doc' ? (value as never) : '', { emitUpdate: false })
      } catch (err) {
        console.warn('[MessageBodyEditor] could not load the body', err)
      }
    }
  }, [editor, value])

  useEffect(() => { editor?.setEditable(!disabled) }, [editor, disabled])

  useImperativeHandle(ref, () => ({
    insertField: (key: string, format?: string) => {
      if (!editor || !key) return
      editor.chain().focus().insertContent({ type: 'mergeField', attrs: { key, format: format || '', fallback: '' } }).run()
    },
    focus: () => { editor?.commands.focus() },
  }), [editor])

  const fieldOptions = useMemo(() => {
    const groups: { label: string; items: { key: string; label: string }[] }[] = []
    if (fields) {
      groups.push({ label: 'This record', items: fields.header.map((c) => ({ key: c.key, label: c.heading })) })
      for (const f of fields.forms) {
        groups.push({ label: `${f.title} (inside its repeat)`, items: f.columns.map((c) => ({ key: c.key, label: c.heading })) })
      }
      for (const l of fields.lookups) {
        groups.push({ label: l.obj, items: l.columns.map((c) => ({ key: `lookup.${l.obj}.${c.key}`, label: c.heading })) })
      }
      groups.push({ label: 'Group', items: fields.group.map((t) => ({ key: t.key, label: t.heading })) })
      groups.push({ label: 'System', items: fields.system.map((t) => ({ key: t.key, label: t.heading })) })
    }
    return groups
  }, [fields])

  const setLink = () => {
    if (!editor) return
    const prev = (editor.getAttributes('link') as { href?: string }).href || ''
    const href = window.prompt('Link address (https://…)', prev)
    if (href === null) return
    if (!href.trim()) editor.chain().focus().unsetLink().run()
    else if (/^(https?:|mailto:|tel:)/i.test(href.trim())) {
      editor.chain().focus().setLink({ href: href.trim() }).run()
    }
  }

  const active = (name: string) => (editor?.isActive(name) ? {
    background: 'var(--color-accent-weak, rgba(0,97,208,.10))', borderColor: 'var(--color-accent, #0061D0)',
  } : {})

  return (
    <div style={{ border: '1px solid var(--color-border-strong, #c9d1db)', borderRadius: 8, background: 'var(--color-input-bg, #fff)' }}>
      <div role="toolbar" aria-label="Formatting" style={{
        display: 'flex', gap: 2, padding: '4px 6px', flexWrap: 'wrap', alignItems: 'center',
        borderBottom: '1px solid var(--color-border, #e6eaef)', background: 'var(--color-bg-secondary, #f0f3f7)',
        borderRadius: '8px 8px 0 0',
      }}>
        <button type="button" style={{ ...toolBtn, ...active('bold') }} title="Bold" disabled={disabled}
                onClick={() => editor?.chain().focus().toggleBold().run()}><Bold size={14} /></button>
        <button type="button" style={{ ...toolBtn, ...active('italic') }} title="Italic" disabled={disabled}
                onClick={() => editor?.chain().focus().toggleItalic().run()}><Italic size={14} /></button>
        <button type="button" style={{ ...toolBtn, ...active('underline') }} title="Underline" disabled={disabled}
                onClick={() => editor?.chain().focus().toggleUnderline().run()}><UnderlineIcon size={14} /></button>
        <button type="button" style={{ ...toolBtn, ...active('link') }} title="Link" disabled={disabled}
                onClick={setLink}><Link2 size={14} /> Link</button>
        <button type="button" style={toolBtn} title="Table" disabled={disabled}
                onClick={() => editor?.chain().focus().insertTable({ rows: 2, cols: 3, withHeaderRow: true }).run()}>
          <TableIcon size={14} /> Table
        </button>
        <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--color-border-strong, #c9d1db)', margin: '4px' }} />
        <select aria-label="Insert a field" style={toolSelect} value="" disabled={disabled || !fields}
                onChange={(e) => {
                  const k = e.target.value
                  if (k) editor?.chain().focus().insertContent({ type: 'mergeField', attrs: { key: k, format: '', fallback: '' } }).run()
                }}>
          <option value="">+ Field</option>
          {fieldOptions.map((g) => (
            <optgroup key={g.label} label={g.label}>
              {g.items.map((it) => <option key={it.key} value={it.key}>{it.label}</option>)}
            </optgroup>
          ))}
        </select>
        <select aria-label="Insert a repeat" style={toolSelect} value="" disabled={disabled}
                onChange={(e) => {
                  const k = e.target.value
                  if (k) editor?.chain().focus().insertContent({ type: 'repeatBlock', attrs: { key: k }, content: [{ type: 'paragraph' }] }).run()
                }}>
          <option value="">+ Repeat</option>
          <option value="records">For each record in the message</option>
          {(fields?.forms || []).map((f) => (
            <option key={f.form_no} value={`form:${f.form_no}`}>For each line of {f.title}</option>
          ))}
        </select>
        {onInclude && (
          <button type="button" style={toolBtn} disabled={disabled} onClick={onInclude}>
            + Include report / visual
          </button>
        )}
      </div>
      <div className="twa-msg-body" style={{ padding: '10px 14px', minHeight, fontSize: 14, lineHeight: 1.7 }}>
        <style>{`.twa-msg-body .ProseMirror{outline:none;min-height:${minHeight - 20}px}
.twa-msg-body .ProseMirror p{margin:0 0 8px}
.twa-msg-body table{border-collapse:collapse;width:100%;margin:6px 0}
.twa-msg-body th,.twa-msg-body td{border:1px solid var(--color-border,#d8dee6);padding:4px 8px;vertical-align:top}
.twa-msg-body th{background:var(--color-bg-secondary,#f0f3f7);font-weight:600}`}</style>
        <EditorContent editor={editor} />
      </div>
    </div>
  )
})

export default MessageBodyEditor
